import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { apiFetch } from '../lib/api';

type AchievementApiItem = {
  key: string;
  leader: string;
  runner_up: string;
  leader_value: number | string | null;
  leader_unit: string | null;
  leader_replay_url?: string | null;
};

type HallOfFameItem = {
  key: string;
  title: string;
  entries: Array<{
    player: string;
    details: string;
    entry_type: 'player' | 'squad';
  }>;
};

const achievementsConfig = [
  {
    key: 'blitzkrieg',
    title: 'Блицкриг',
    image: '/static/img/ks_ach.svg',
    accent: 'from-red-500/50 via-red-600/40 to-red-700/60',
  },
  {
    key: 'aviator',
    title: 'Авиатор',
    image: '/static/img/jet.svg',
    accent: 'from-sky-400/40 via-sky-500/40 to-sky-600/60',
  },
  {
    key: 'big_sniper',
    title: 'Зоркий глаз',
    image: '/static/img/big_snp.svg',
    accent: 'from-amber-400/40 via-amber-500/40 to-amber-600/60',
  },
  {
    key: 'terminator',
    title: 'Terminator',
    image: '/static/img/max_inf.svg',
    accent: 'from-emerald-400/40 via-emerald-500/40 to-emerald-600/60',
  },
  {
    key: 'sharp_eye',
    title: 'Меткий глаз',
    image: '/static/img/sm_snp.svg',
    accent: 'from-indigo-400/40 via-indigo-500/40 to-indigo-600/60',
  },
  {
    key: 'omp',
    title: 'ОМП',
    image: '/static/img/veh_frag.svg',
    accent: 'from-orange-400/40 via-orange-500/40 to-orange-600/60',
  },
  {
    key: 'guardian',
    title: 'Ангел-хранитель',
    image: '/static/img/med.svg',
    accent: 'from-blue-400/40 via-blue-500/40 to-blue-700/60',
  },
];

const formatLeaderValue = (value: AchievementApiItem['leader_value'], unit: AchievementApiItem['leader_unit']) => {
  if (value === null || value === undefined || value === '') {
    return '—';
  }
  if (unit) {
    return `${value} ${unit}`;
  }
  return String(value);
};

export function AchievementsPage(): JSX.Element {
  const { data } = useQuery({
    queryKey: ['achievements'],
    queryFn: () => apiFetch<{ items: AchievementApiItem[]; hall_of_fame: HallOfFameItem[] }>('/achievements/'),
  });

  const achievements = useMemo(() => {
    const mapped = new Map(data?.items.map((item) => [item.key, item]) ?? []);
    return achievementsConfig.map((achievement) => {
      const apiItem = mapped.get(achievement.key);
      return {
        ...achievement,
        player: apiItem?.leader ?? 'Ждём данных..',
        runnerUp: apiItem?.runner_up ?? 'Ждём данных..',
        leaderValue: apiItem?.leader_value ?? null,
        leaderUnit: apiItem?.leader_unit ?? null,
        leaderReplayUrl: apiItem?.leader_replay_url ?? null,
      };
    });
  }, [data]);

  const hallOfFame = data?.hall_of_fame ?? [];

  return (
    <main className="mx-auto w-full max-w-7xl space-y-6 px-4 py-6 sm:px-6">
      <div className="space-y-2">
        <p className="text-sm text-muted-foreground">Текущая ротация</p>
        <p className="text-3xl font-bold">Достижения</p>
        <p className="text-sm text-muted-foreground">
          Семь ключевых ачивок и их текущие лидеры. Победители будут определены по завершении ротации.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {achievements.map((achievement, idx) => (
          <motion.div
            key={achievement.title}
            className="relative overflow-hidden rounded-xl border border-border bg-card shadow"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.18, delay: idx * 0.02 }}
          >
            <div
              className="absolute inset-0 bg-contain bg-center bg-no-repeat"
              style={{ backgroundImage: `url(${achievement.image})` }}
              aria-hidden
            />
            <div className={`absolute inset-0 bg-gradient-to-br ${achievement.accent}`} aria-hidden />
            <div className="relative flex min-h-[200px] flex-col justify-end bg-black/30 p-4 text-white backdrop-blur-[2px]">
              {achievement.player === 'Ждём данных..' ? (
                <p className="text-2xl font-bold drop-shadow">{achievement.player}</p>
              ) : (
                <a
                  className="text-2xl font-bold drop-shadow underline-offset-2 hover:underline"
                  href={`/player/${encodeURIComponent(achievement.player)}`}
                >
                  {achievement.player}
                </a>
              )}
              <p className="text-lg font-semibold drop-shadow-sm">{achievement.title}</p>
              <p className="text-sm font-medium text-slate-100/90 drop-shadow-sm">
                Текущий показатель: {formatLeaderValue(achievement.leaderValue, achievement.leaderUnit)}
              </p>
              {achievement.leaderReplayUrl ? (
                <a
                  className="text-sm font-medium text-slate-100/90 underline-offset-2 hover:underline"
                  href={achievement.leaderReplayUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  Реплей
                </a>
              ) : null}
              <p className="text-sm font-medium text-slate-100/90 drop-shadow-sm">Ближайший преследователь: {achievement.runnerUp}</p>
            </div>
          </motion.div>
        ))}
      </div>

      <section className="space-y-4">
        <div className="space-y-1">
          <p className="text-sm text-muted-foreground">История достижений</p>
          <h2 className="text-2xl font-bold">Зал славы</h2>
          <p className="text-sm text-muted-foreground">
            Все победители категорий и обладатели ачивок за прошедшие ротации.
          </p>
        </div>
        <div className="grid gap-4 lg:grid-cols-2">
          {hallOfFame.map((category) => (
            <div key={category.key} className="rounded-xl border border-border bg-card p-4 shadow-sm">
              <p className="text-lg font-semibold">{category.title}</p>
              <div className="mt-3 space-y-3">
                {category.entries.map((entry, idx) => (
                  <div key={`${entry.player}-${idx}`} className="rounded-lg border border-border/60 bg-background/50 p-3">
                    {entry.entry_type === 'squad' ? (
                      <a
                        className="text-sm font-semibold underline-offset-2 hover:underline"
                        href={`/squad-stats/${encodeURIComponent(entry.player)}`}
                      >
                        {entry.player}
                      </a>
                    ) : (
                      <a
                        className="text-sm font-semibold underline-offset-2 hover:underline"
                        href={`/player/${encodeURIComponent(entry.player)}`}
                      >
                        {entry.player}
                      </a>
                    )}
                    <p className="text-sm text-muted-foreground">{entry.details}</p>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>
    </main>
  );
}

export default AchievementsPage;
